// R117 (Jesse: "a cute halloween related feature on their portal, maybe a
// cute ghost that you can interact with a bit, poke, pull around"): a little
// ghost that floats in the corner of the owner portal during October. Poke it
// (tap / click) and it giggles and wiggles; grab it and pull it anywhere; it
// drifts home after a while. Reduced motion: no float, no wiggle — still
// draggable. One element, one rAF loop that runs only while it moves, and a ✕
// that puts it away for the session.

const KEY = 'op_ghost_hidden';
const LINES = ['Boo! 👻', 'Hehe, that tickles!', 'Happy Halloween!', 'Your home is in good hands 🏡', 'Wheee!', 'Boo-tiful stays ahead!', 'Trick or treat? 🍬', 'I live in the guest closet…'];

let mounted: HTMLElement | null = null;

export function ghostEnabled(featureOn: boolean, now = new Date()): boolean {
  if (!featureOn) return false;
  if (now.getMonth() !== 9) return false;   // October only
  try { if (sessionStorage.getItem(KEY) === '1') return false; } catch (_) { /* private mode */ }
  return true;
}

export function unmountGhost(): void {
  if (mounted) { mounted.remove(); mounted = null; }
}

// Mounted INSIDE the portal page (a redraw or leaving the page removes it) —
// re-mount after each draw; a still-attached ghost is left where it is.
export function mountGhost(parent: HTMLElement): void {
  if (typeof document === 'undefined') return;
  if (mounted && mounted.isConnected) return;
  mounted = null;
  const reduce = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const host = document.createElement('div');
  host.className = 'opg';
  host.setAttribute('role', 'img');
  host.setAttribute('aria-label', 'A friendly Halloween ghost — poke it or drag it around');
  host.innerHTML = `
    <style>
      .opg { position:fixed; right:22px; bottom:22px; width:84px; height:96px; z-index:60; touch-action:none; user-select:none; -webkit-user-select:none; cursor:grab; }
      .opg.drag { cursor:grabbing; }
      .opg-body { width:100%; height:100%; transform-origin:50% 90%; filter:drop-shadow(0 8px 14px rgba(15,23,42,.18)); }
      .opg.float .opg-body { animation:opg-float 3.2s ease-in-out infinite; }
      .opg.wiggle .opg-body { animation:opg-wiggle .55s ease-in-out; }
      @keyframes opg-float { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-8px); } }
      @keyframes opg-wiggle { 0% { transform:rotate(0) scale(1); } 25% { transform:rotate(-12deg) scale(1.08,.94); } 50% { transform:rotate(10deg) scale(.95,1.06); } 75% { transform:rotate(-6deg); } 100% { transform:rotate(0) scale(1); } }
      .opg-say { position:absolute; right:70px; bottom:70px; width:max-content; max-width:180px; padding:7px 11px; border-radius:12px 12px 2px 12px; background:var(--card, #fff); color:var(--text, #0f172a); font:600 12.5px/1.35 Inter, system-ui, sans-serif; box-shadow:0 6px 18px rgba(15,23,42,.14); border:1px solid var(--border, #e5e9f2); opacity:0; transform:translateY(4px); transition:opacity .2s, transform .2s; pointer-events:none; white-space:normal; }
      .opg-say.on { opacity:1; transform:none; }
      .opg-x { position:absolute; top:-6px; right:-6px; width:22px; height:22px; border-radius:50%; border:1px solid var(--border, #e5e9f2); background:var(--card, #fff); color:var(--text-muted, #64748b); font-size:12px; line-height:20px; text-align:center; cursor:pointer; opacity:0; transition:opacity .15s; padding:0; }
      .opg:hover .opg-x, .opg:focus-within .opg-x { opacity:1; }
      @media (pointer: coarse) { .opg-x { opacity:1; } }
      @media (max-width: 640px) { .opg { right:12px; bottom:84px; width:66px; height:76px; } .opg-say { right:56px; bottom:56px; } }
    </style>
    <div class="opg-say" aria-live="polite"></div>
    <svg class="opg-body" viewBox="0 0 84 96" aria-hidden="true">
      <path d="M42 4C22 4 10 20 10 40v44c0 4 4 6 7 3l6-5 6 6c2 2 5 2 7 0l6-6 6 6c2 2 5 2 7 0l6-6 6 5c3 3 7 1 7-3V40C74 20 62 4 42 4z" fill="#ffffff" stroke="#c7d2fe" stroke-width="2"/>
      <ellipse class="opg-eye" cx="32" cy="38" rx="4.5" ry="6" fill="#1e293b"/>
      <ellipse class="opg-eye" cx="52" cy="38" rx="4.5" ry="6" fill="#1e293b"/>
      <circle cx="30.5" cy="35.5" r="1.4" fill="#fff"/><circle cx="50.5" cy="35.5" r="1.4" fill="#fff"/>
      <ellipse cx="25" cy="49" rx="5" ry="3" fill="#fbcfe8" opacity=".8"/><ellipse cx="59" cy="49" rx="5" ry="3" fill="#fbcfe8" opacity=".8"/>
      <path class="opg-mouth" d="M36 50q6 6 12 0" fill="none" stroke="#1e293b" stroke-width="2.4" stroke-linecap="round"/>
    </svg>
    <button type="button" class="opg-x" aria-label="Hide the ghost">✕</button>`;
  parent.appendChild(host);
  mounted = host;
  if (!reduce) host.classList.add('float');
  const say = host.querySelector('.opg-say') as HTMLElement;
  let sayTimer: ReturnType<typeof setTimeout> | null = null;
  let n = 0;
  const speak = (text: string) => {
    say.textContent = text; say.classList.add('on');
    if (sayTimer) clearTimeout(sayTimer);
    sayTimer = setTimeout(() => say.classList.remove('on'), 1800);
  };
  const poke = () => {
    speak(LINES[n++ % LINES.length]);
    if (reduce) return;
    host.classList.remove('wiggle'); void host.offsetWidth; host.classList.add('wiggle');
  };
  host.addEventListener('animationend', (e) => { if ((e as AnimationEvent).animationName === 'opg-wiggle') host.classList.remove('wiggle'); });
  host.querySelector('.opg-x')!.addEventListener('click', (e) => {
    e.stopPropagation();
    try { sessionStorage.setItem(KEY, '1'); } catch (_) { /* */ }
    unmountGhost();
  });

  // drag: pointer events, a move threshold separates a poke from a pull
  let start: { x: number; y: number; left: number; top: number } | null = null;
  let moved = false;
  let homeTimer: ReturnType<typeof setTimeout> | null = null;
  host.addEventListener('pointerdown', (e) => {
    if ((e.target as HTMLElement).closest('.opg-x')) return;
    const r = host.getBoundingClientRect();
    start = { x: e.clientX, y: e.clientY, left: r.left, top: r.top };
    moved = false;
    host.setPointerCapture(e.pointerId);
  });
  host.addEventListener('pointermove', (e) => {
    if (!start) return;
    const dx = e.clientX - start.x, dy = e.clientY - start.y;
    if (!moved && Math.hypot(dx, dy) < 6) return;
    if (!moved) { moved = true; host.classList.add('drag'); host.classList.remove('float'); if (homeTimer) clearTimeout(homeTimer); }
    const w = host.offsetWidth, h = host.offsetHeight;
    const left = Math.max(4, Math.min(window.innerWidth - w - 4, start.left + dx));
    const top = Math.max(4, Math.min(window.innerHeight - h - 4, start.top + dy));
    host.style.left = `${left}px`; host.style.top = `${top}px`; host.style.right = 'auto'; host.style.bottom = 'auto';
  });
  const end = (e: PointerEvent) => {
    if (!start) return;
    try { host.releasePointerCapture(e.pointerId); } catch (_) { /* */ }
    start = null;
    host.classList.remove('drag');
    if (!moved) { poke(); return; }
    speak('Wheee! 👻');
    if (!reduce) host.classList.add('float');
    // drift home after a while so it never blocks the page for long
    homeTimer = setTimeout(() => { if (!mounted) return; host.style.transition = reduce ? '' : 'left .6s ease, top .6s ease'; host.style.left = ''; host.style.top = ''; host.style.right = ''; host.style.bottom = ''; setTimeout(() => { host.style.transition = ''; }, 700); }, 20000);
  };
  host.addEventListener('pointerup', end);
  host.addEventListener('pointercancel', end);
  host.tabIndex = 0;
  host.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); poke(); } });
}
